import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useNavigate } from "react-router-dom";
import { Layers, Hammer, FileCheck2, PencilRuler, Loader2, X } from "lucide-react";

const TYPE_META = {
  design_build: { icon: Hammer, tint: "#4f46e5", bg: "bg-indigo-50", text: "text-indigo-700" },
  permit_only: { icon: FileCheck2, tint: "#d97706", bg: "bg-amber-50", text: "text-amber-700" },
  design_only: { icon: PencilRuler, tint: "#0891b2", bg: "bg-cyan-50", text: "text-cyan-700" },
};
const money = (n) => (n >= 1000000 ? `$${(n / 1000000).toFixed(2)}M` : `$${Math.round(n / 1000)}k`);

export const ServiceTypeBoard = ({ region, regions = [] }) => {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [drill, setDrill] = useState(null); // {branch, branchLabel, type, typeLabel}
  const [drillLeads, setDrillLeads] = useState(null);
  useEffect(() => {
    let current = true;
    setData(null);
    setErr(false);
    const query = region ? `?region=${encodeURIComponent(region)}` : "";
    api.get(`/crm/service-type-board${query}`).then(({ data }) => { if (current) setData(data); }).catch(() => { if (current) setErr(true); });
    return () => { current = false; };
  }, [api, region]);

  const openDrill = async (branch, branchLabel, t) => {
    setDrill({ branch, branchLabel, type: t.key, typeLabel: zh ? t.zh : t.en });
    setDrillLeads(null);
    try { const { data } = await api.get(`/crm/service-type-board/leads?branch=${encodeURIComponent(branch)}&serviceType=${t.key}${region ? `&region=${encodeURIComponent(region)}` : ""}`); setDrillLeads(data.leads || []); }
    catch { setDrillLeads([]); }
  };

  if (err || !data) return null;
  const types = data.serviceTypes || [];
  const branches = data.branches;
  const selectedLabel = regions.find((item) => item.id === region);
  const totals = types.reduce((acc, t) => { acc[t.key] = { count: 0, value: 0 }; return acc; }, {});
  data.branches.forEach((b) => types.forEach((t) => { totals[t.key].count += b.types[t.key].count; totals[t.key].value += b.types[t.key].value; }));

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="service-type-board">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="h-4 w-4 text-indigo-500" />
        <h3 className="text-sm font-semibold text-slate-800">{region ? (zh ? `${selectedLabel?.zh || region} · 客户类型分布` : `${selectedLabel?.en || region} · Client Type Mix`) : (zh ? "分公司 · 客户类型分布" : "Branch · Client Type Mix")}</h3>
        <span className="text-[11px] text-slate-400 ml-1">{zh ? (region ? `仅当前公司 · 点击数字查看客户列表` : "全公司分布 · 点击数字查看客户列表") : (region ? "Selected company · click a cell for leads" : "All companies · click a cell for leads")}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {types.map((t) => { const M = TYPE_META[t.key]; const Icon = M.icon; return (
          <div key={t.key} className={`${M.bg} rounded-lg p-2.5`} data-testid={`stb-total-${t.key}`}>
            <div className={`flex items-center gap-1.5 text-[11px] font-medium ${M.text}`}><Icon className="h-3.5 w-3.5" />{zh ? t.zh : t.en}</div>
            <p className="text-lg font-bold text-slate-900 mt-0.5">{totals[t.key].count} <span className="text-xs font-normal text-slate-400">· {money(totals[t.key].value)}</span></p>
          </div>
        ); })}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] text-slate-400 border-b border-slate-100">
              <th className="text-left py-1.5 font-medium">{zh ? "分公司" : "Branch"}</th>
              {types.map((t) => <th key={t.key} className="text-right py-1.5 font-medium">{zh ? t.zh.split(" ")[0].slice(0, 6) : t.en.split(" ")[0]}</th>)}
              <th className="text-right py-1.5 font-medium">{zh ? "合计" : "Total"}</th>
            </tr>
          </thead>
          <tbody>
            {branches.map((b) => (
              <tr key={b.branch} className={`border-b border-slate-50 last:border-0 ${region === b.branch ? "bg-indigo-50 font-semibold" : ""}`} data-testid={`stb-row-${b.branch}`} data-selected={region === b.branch ? "true" : "false"}>
                <td className="py-1.5 font-medium text-slate-700">{zh ? b.branchZh : b.branchEn}</td>
                {types.map((t) => (
                  <td key={t.key} className="text-right py-1.5 text-slate-600">
                    {b.types[t.key].count > 0
                      ? <button data-testid={`stb-cell-${b.branch}-${t.key}`} onClick={() => openDrill(b.branch, zh ? b.branchZh : b.branchEn, t)} className="hover:underline decoration-indigo-400 underline-offset-2"><b className="text-slate-800">{b.types[t.key].count}</b> <span className="text-[10px] text-slate-400">{money(b.types[t.key].value)}</span></button>
                      : <span className="text-slate-300">–</span>}
                  </td>
                ))}
                <td className="text-right py-1.5 font-semibold text-indigo-600">{b.totalCount} <span className="text-[10px] font-normal text-slate-400">{money(b.totalValue)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {drill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="stb-drill-dialog" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <p className="text-sm font-semibold text-slate-800">{drill.branchLabel} · {drill.typeLabel}</p>
              <button onClick={() => setDrill(null)} data-testid="stb-drill-close" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            </div>
            <div className="overflow-y-auto p-3">
              {drillLeads === null ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500" /></div>
                : drillLeads.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">{zh ? "暂无客户" : "No leads"}</p>
                : drillLeads.map((l) => (
                  <button key={l.id} data-testid={`stb-drill-lead-${l.id}`} onClick={() => { setDrill(null); nav(`/crm/leads/${l.id}`); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 flex items-center justify-between gap-3">
                    <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{l.clientName}</p><p className="text-[11px] text-slate-400 truncate">{l.address || "-"} · {l.assignedSales || (zh ? "未指派" : "unassigned")}</p></div>
                    <span className="text-xs font-semibold text-indigo-600 shrink-0">{money(l.value)}</span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceTypeBoard;

